import React from 'react';
import Icon from '../../../components/AppIcon';

const EESCenterOverview = ({ center = {}, isSaved = false, onSave, onShare, onContact }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  return (
    <div className="bg-surface rounded-lg shadow-elevation-1 overflow-hidden">
      <div className="p-6">
        {/* Header Section */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <h1 className="text-2xl lg:text-3xl font-bold text-text-primary mb-2 font-heading">
              {center?.title}
            </h1>
            <div className="flex items-center space-x-2 text-text-secondary mb-3">
              <Icon name="MapPin" size={18} className="text-accent" />
              <span>{center?.address}</span>
            </div>
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-1">
                <Icon name="Zap" size={16} className="text-primary" />
                <span className="font-medium text-text-primary">{center?.eesUnits} EES Units</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Users" size={16} className="text-primary" />
                <span className="font-medium text-text-primary">Up to {center?.capacity} guests</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Award" size={16} className="text-primary" />
                <span className="font-medium text-text-primary">{center?.certifications?.length} Certifications</span>
              </div>
            </div>
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-3 ml-6">
            <button
              onClick={onSave}
              className={`p-3 rounded-md transition-all duration-200 ${
                isSaved 
                  ? 'bg-error text-white' :'bg-secondary-100 text-text-secondary hover:bg-error hover:text-white'
              }`}
              title={isSaved ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Icon name="Heart" size={20} fill={isSaved ? "currentColor" : "none"} />
            </button>
            <button
              onClick={onShare}
              className="p-3 bg-secondary-100 text-text-secondary rounded-md hover:bg-secondary-200 transition-all duration-200"
              title="Share center"
            >
              <Icon name="Share" size={20} />
            </button>
          </div>
        </div>

        {/* Pricing Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-primary-50 p-4 rounded-lg border border-primary-100">
            <div className="text-sm text-primary font-medium mb-1">Single Session</div>
            <div className="text-2xl font-bold text-primary">{formatPrice(center?.pricing?.singleSession)}</div>
            <div className="text-xs text-primary-700">2-hour session</div>
          </div>
          <div className="bg-accent-100 p-4 rounded-lg border border-accent-100">
            <div className="text-sm text-accent-600 font-medium mb-1">Day Pass</div>
            <div className="text-2xl font-bold text-accent-600">{formatPrice(center?.pricing?.dayPass)}</div>
            <div className="text-xs text-accent-600">Unlimited sessions</div>
          </div>
          <div className="bg-success-100 p-4 rounded-lg border border-success-100">
            <div className="text-sm text-success-600 font-medium mb-1">Weekly Package</div>
            <div className="text-2xl font-bold text-success-600">{formatPrice(center?.pricing?.weeklyPackage)}</div>
            <div className="text-xs text-success-600">7 days access</div>
          </div>
          <div className="bg-warning-100 p-4 rounded-lg border border-warning-100">
            <div className="text-sm text-warning-600 font-medium mb-1">Monthly Unlimited</div>
            <div className="text-2xl font-bold text-warning-600">{formatPrice(center?.pricing?.monthlyUnlimited)}</div>
            <div className="text-xs text-warning-600">Best value</div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-text-primary mb-1">{center?.rating || '4.9'}</div>
            <div className="text-sm text-text-secondary">Rating</div>
            <div className="flex justify-center mt-1">
              {[...Array(5)].map((_, i) => (
                <Icon
                  key={i}
                  name="Star"
                  size={14}
                  className={i < Math.floor(center?.rating || 4.9) ? 'text-warning fill-current' : 'text-secondary-300'}
                />
              ))}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-text-primary mb-1">{center?.reviewsCount || '127'}</div>
            <div className="text-sm text-text-secondary">Reviews</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-text-primary mb-1">{center?.yearsExperience || '8'}</div>
            <div className="text-sm text-text-secondary">Years Experience</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-text-primary mb-1">{center?.completedSessions || '2.5k'}</div>
            <div className="text-sm text-text-secondary">Sessions Completed</div>
          </div>
        </div>

        {/* Main Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button className="flex-1 flex items-center justify-center space-x-2 bg-primary text-white py-4 rounded-md hover:bg-primary-700 transition-all duration-200 font-medium">
            <Icon name="Calendar" size={18} />
            <span>Book Session</span>
          </button>
          <button 
            onClick={onContact}
            className="flex-1 flex items-center justify-center space-x-2 bg-accent text-white py-4 rounded-md hover:bg-accent-600 transition-all duration-200 font-medium"
          >
            <Icon name="MessageCircle" size={18} />
            <span>Contact Host</span>
          </button>
        </div>

        {/* Wellness Badges */}
        <div className="flex flex-wrap gap-2 mt-4">
          {center?.badges?.map((badge, index) => (
            <span
              key={index}
              className="inline-flex items-center space-x-1 px-3 py-1 bg-success-100 text-success-600 rounded-full text-xs font-medium"
            >
              <Icon name="CheckCircle" size={12} />
              <span>{badge}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EESCenterOverview;